'use client'

import { getOptions, optionLabel, valueState, type OptionType, type OptionValues } from '@/lib/commerce/variantOptions'
import { cn } from '@/lib/utils'

type VariantOptionPickerProps = {
  product: any
  selected: OptionValues
  onSelect: (_type: OptionType, _value: string) => void
  tone?: 'light' | 'dark'
  // Selected size/custom chip look — templates pass their accent color here.
  selectedClassName?: string
  selectedStyle?: React.CSSProperties
}

// Color swatches, then size and custom chips. Sold-out and non-existent
// combinations stay clickable but are crossed out.
export default function VariantOptionPicker({ product, selected, onSelect, tone = 'light', selectedClassName, selectedStyle }: VariantOptionPickerProps) {
  const dark = tone === 'dark'
  return (
    <div className="space-y-4">
      {getOptions(product).map((option) => (
        <div key={option.type}>
          <div className={cn('text-sm font-medium', dark ? 'text-white/70' : 'text-foreground')}>
            {optionLabel(option)}
            {selected[option.type] && <span className={dark ? 'text-white' : 'text-muted-foreground'}>: {selected[option.type]}</span>}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {option.values.map(({ value, hex }) => {
              const isSelected = selected[option.type] === value
              const unavailable = valueState(product, selected, option.type, value) !== 'available'
              if (option.type === 'color') {
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => onSelect('color', value)}
                    aria-label={value}
                    aria-pressed={isSelected}
                    title={value}
                    className={cn('relative size-9 rounded-full p-0.5 border-2 transition-colors cursor-pointer', isSelected ? (dark ? 'border-white' : 'border-foreground') : 'border-transparent', unavailable && 'opacity-40')}
                  >
                    <span className={cn('block size-full rounded-full border', dark ? 'border-white/20' : 'border-black/10')} style={{ backgroundColor: hex }} />
                    {unavailable && <span className={cn('absolute left-1/2 top-1/2 h-0.5 w-full -translate-x-1/2 -translate-y-1/2 -rotate-45', dark ? 'bg-white/80' : 'bg-foreground/60')} />}
                  </button>
                )
              }
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => onSelect(option.type, value)}
                  aria-pressed={isSelected}
                  className={cn(
                    'min-w-11 h-10 px-3 rounded-md border text-sm font-medium transition-colors cursor-pointer',
                    isSelected
                      ? selectedClassName ?? (dark ? 'bg-white text-[#0a0a0c] border-white' : 'bg-foreground text-white border-foreground')
                      : dark ? 'border-white/15 text-white hover:border-white/40' : 'border-border bg-muted text-foreground hover:bg-muted-foreground/20',
                    unavailable && 'line-through opacity-50',
                  )}
                  style={isSelected ? selectedStyle : undefined}
                >
                  {value}
                </button>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}
